import type {
  AccountListDetail,
  AccountListSummary,
  AccountSummary,
  LedgerItem,
  ProxyItem,
  SubConnection,
  SubOperationLog,
  TaskDetail,
  TaskSummary,
  User,
} from "./types";

const now = Date.now();
const iso = (offsetMinutes: number) => new Date(now - offsetMinutes * 60_000).toISOString();

const tasks: TaskSummary[] = [
  { id: 15024, user_id: 0, status: "running", total_count: 80, success_count: 46, failed_count: 4, charged_count: 46, concurrency: 10, created_at: iso(18), started_at: iso(17), finished_at: null },
  { id: 15023, user_id: 0, status: "completed", total_count: 36, success_count: 34, failed_count: 2, charged_count: 34, concurrency: 6, created_at: iso(190), started_at: iso(188), finished_at: iso(154) },
  { id: 15022, user_id: 0, status: "partial_failed", total_count: 64, success_count: 51, failed_count: 13, charged_count: 51, concurrency: 8, created_at: iso(520), started_at: iso(517), finished_at: iso(448) },
];

const taskDetails: Record<number, TaskDetail> = {
  15024: {
    ...tasks[0],
    items: Array.from({ length: 12 }, (_, index) => ({
      item_id: 88000 + index,
      line_no: index + 1,
      account_id: 3200 + index,
      status: index < 8 ? "success" : index < 10 ? "running" : "pending",
      stage: index < 8 ? "token_exchange" : index < 10 ? "wait_otp" : "queued",
      error_code: null,
      error_message: null,
      charged: index < 8,
      sync_status: index < 8 ? "synced" : null,
    })),
  },
  15023: {
    ...tasks[1],
    items: Array.from({ length: 8 }, (_, index) => ({
      item_id: 87000 + index,
      line_no: index + 1,
      account_id: 3100 + index,
      status: index < 6 ? "success" : "failed",
      stage: index < 6 ? "token_exchange" : "wait_otp",
      error_code: index < 6 ? null : "otp_timeout",
      error_message: index < 6 ? null : "等待验证码超时",
      charged: index < 6,
    })),
  },
  15022: {
    ...tasks[2],
    items: Array.from({ length: 8 }, (_, index) => ({
      item_id: 86000 + index,
      line_no: index + 1,
      account_id: 3000 + index,
      status: index < 5 ? "success" : "failed",
      stage: index < 5 ? "token_exchange" : "mailbox_probe",
      error_code: index < 5 ? null : "mailapi_unreachable",
      error_message: index < 5 ? null : "接码服务暂时无法访问",
      charged: index < 5,
    })),
  },
};

const accountLists: AccountListSummary[] = [
  { id: 41, name: "7 月待重授权", status: "active", account_count: 128, success_count: 92, running_count: 10, failed_count: 11, created_at: iso(8200), updated_at: iso(4) },
  { id: 38, name: "Sub2 异常账号", status: "active", account_count: 64, success_count: 51, running_count: 0, failed_count: 13, created_at: iso(18000), updated_at: iso(448) },
  { id: 31, name: "稳定账号归档", status: "active", account_count: 220, success_count: 218, running_count: 0, failed_count: 2, created_at: iso(34000), updated_at: iso(1440) },
];

function previewAccounts(list: AccountListSummary, count: number): AccountSummary[] {
  return Array.from({ length: count }, (_, index) => {
    const status = index % 9 === 0 ? "failed" : index % 7 === 0 ? "success" : "active";
    return {
      id: list.id * 1000 + index + 1,
      list_id: list.id,
      email: `preview-${String(index + 1).padStart(3, "0")}@example.test`,
      login_identity: `preview-${String(index + 1).padStart(3, "0")}@example.test`,
      mailbox_url_masked: "https://mail.example.test/***",
      status,
      last_task_id: status === "active" ? null : 15000 + index,
      last_task_item_id: status === "active" ? null : 85000 + index,
      last_success_at: status === "success" ? iso(120 + index) : null,
      last_error: status === "failed" ? "预览诊断：等待验证码超时" : "",
      created_at: list.created_at,
      updated_at: list.updated_at,
    };
  });
}

const accountDetails: Record<number, AccountListDetail> = Object.fromEntries(
  accountLists.map((list, index) => [list.id, { list, accounts: previewAccounts(list, 18 + index * 3) }]),
);

const connections: SubConnection[] = [
  { id: 6, name: "东京主节点", mode: "direct", sub_base_url: "http://10.8.0.12:8080", admin_key_masked: "adm••••••8f2", status: "active", last_checked_at: iso(6), created_at: iso(18000), updated_at: iso(6) },
  { id: 4, name: "归档节点", mode: "direct", sub_base_url: "http://10.8.0.21:8080", admin_key_masked: "adm••••••7b1", status: "active", last_checked_at: iso(84), created_at: iso(28000), updated_at: iso(84) },
];

const proxies: ProxyItem[] = [
  { id: 19, scheme: "http", host: "jp-a.example", port: 3128, label: "JP-A", masked_url: "http://user:***@jp-a.example:3128", status: "active", created_at: iso(14000), updated_at: iso(10) },
  { id: 18, scheme: "socks5", host: "jp-b.example", port: 1080, label: "JP-B", masked_url: "socks5://user:***@jp-b.example:1080", status: "active", created_at: iso(14000), updated_at: iso(14) },
  { id: 17, scheme: "http", host: "sg-a.example", port: 8080, label: "SG-A", masked_url: "http://user:***@sg-a.example:8080", status: "active", created_at: iso(16000), updated_at: iso(31) },
];

const logs: SubOperationLog[] = [
  { id: 201, sub_connection_id: 6, operation: "scan", status: "success", created_at: iso(12) },
  { id: 200, sub_connection_id: 6, operation: "replace", target_account_id: "sub-8821", status: "success", created_at: iso(24) },
  { id: 199, sub_connection_id: 4, operation: "test", status: "failed", error: "连接超时，未写入任何账号", created_at: iso(84) },
];

const ledger: LedgerItem[] = [
  { id: 22031, type: "credit_consume", amount: -46, balance_after: 874, remark: "task #15024", created_at: iso(4) },
  { id: 22002, type: "credit_add", amount: 500, balance_after: 920, remark: "redeem OA-••••-7A2C", created_at: iso(240) },
  { id: 21962, type: "credit_consume", amount: -34, balance_after: 420, remark: "task #15023", created_at: iso(154) },
];

export const previewUser: User = { id: 0, username: "preview", role: "operator", credit_balance: 874, task_concurrency_limit: 10 };

export function previewRead(path: string): unknown {
  if (path === "/api/auth/me") return previewUser;
  if (path === "/api/tasks") return { items: tasks };
  if (path === "/api/account-lists") return { items: accountLists };
  const accountListMatch = path.match(/^\/api\/account-lists\/(\d+)$/);
  if (accountListMatch) return accountDetails[Number(accountListMatch[1])];
  if (path === "/api/sub2/connections") return { items: connections };
  if (path === "/api/proxies") return { items: proxies, total: proxies.length };
  if (path === "/api/sub2/operation-logs") return { items: logs };
  if (path === "/api/credits/balance") return { balance: previewUser.credit_balance };
  if (path === "/api/credits/ledger") return { items: ledger };
  const taskMatch = path.match(/^\/api\/tasks\/(\d+)$/);
  if (taskMatch) return taskDetails[Number(taskMatch[1])];
  return undefined;
}
